// src/features/task/TaskCategoriesAdmin.tsx
// Admin: hierarchical task categories. Wraps List/Create/Update/Delete/GetTaskCategory.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useTaskTaskCategory, useTaskTaskType, useTaskTaskNumbering, useI18n } from '@react/hooks';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Input, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { PipelinePicker } from '@/components/PipelinePicker';
import { useScope } from '@/hooks/useScope';
import { taskPriorityOptions } from '@/features/task/logic';
import { diffFields, digitsOnly, makeEntity } from '@/features/task/panelHelpers';
import type { TaskCategory } from '@core/types/task';

const EDITABLE_FIELDS: (keyof TaskCategory)[] = [
  'name', 'code', 'description', 'parent', 'sort_order', 'default_task_type_id', 'default_numbering_scheme', 'default_priority',
  'default_pipeline',
];

interface EditFormState {
  name: string;
  code: string;
  description: string;
  parent_id: string;
  sort_order: string;
  default_task_type_id: string;
  default_numbering_scheme_id: string;
  default_priority: string;
  default_pipeline_id: string;
}

export const TaskCategoriesAdmin = observer(() => {
  const store = useTaskTaskCategory();
  const taskTypeStore = useTaskTaskType();
  const numberingStore = useTaskTaskNumbering();
  const { t } = useI18n();
  const scope = useScope();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  useEffect(() => {
    store.ListTaskCategories();
    taskTypeStore.ListTaskTypes({ pagination: { limit: 100, cursor: '', direction: 'forward' }, sort: [] } as any);
    numberingStore.ListTaskNumberingSchemes();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await store.CreateTaskCategory({
        entity: makeEntity('', 'task_category', scope),
        name: name.trim(),
        description: description.trim() || undefined,
        code: code.trim() || undefined,
      } as any);
      setName(''); setCode(''); setDescription('');
    } catch { /* store.error */ }
  };

  const startEdit = async (id: string) => {
    setEditingId(id);
    try {
      const cat = await store.GetTaskCategory(id);
      setEditForm({
        name: cat?.name || '',
        code: cat?.code || '',
        description: cat?.description || '',
        parent_id: cat?.parent?.id || '',
        sort_order: String(cat?.sort_order ?? 0),
        default_task_type_id: cat?.default_task_type_id || '',
        default_numbering_scheme_id: cat?.default_numbering_scheme?.id || '',
        default_priority: (cat?.default_priority as string) || '',
        default_pipeline_id: cat?.default_pipeline?.id || '',
      });
    } catch {
      setEditForm(null);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async (id: string) => {
    if (!editForm) return;
    const current = store.items.find((c) => c.entity?.id === id) || null;
    const updated: TaskCategory = {
      ...(current || ({} as TaskCategory)),
      name: editForm.name.trim(),
      code: editForm.code.trim() || undefined,
      description: editForm.description.trim(),
      parent: editForm.parent_id ? makeEntity(editForm.parent_id, 'task_category', scope) : undefined,
      sort_order: parseInt(editForm.sort_order || '0', 10),
      default_task_type_id: editForm.default_task_type_id || undefined,
      default_numbering_scheme: editForm.default_numbering_scheme_id
        ? makeEntity(editForm.default_numbering_scheme_id, 'task_numbering_scheme', scope)
        : undefined,
      default_priority: (editForm.default_priority || undefined) as TaskCategory['default_priority'],
      default_pipeline: editForm.default_pipeline_id ? makeEntity(editForm.default_pipeline_id, 'pipeline', scope) : undefined,
    };
    const paths = diffFields(current, updated, EDITABLE_FIELDS);
    if (paths.length === 0) { cancelEdit(); return; }
    try {
      await store.UpdateTaskCategory(id, { category: updated, field_mask: paths } as any);
      cancelEdit();
    } catch { /* store.error */ }
  };

  const remove = async (id: string) => {
    try { await store.DeleteTaskCategory(id); setConfirmId(null); } catch { /* store.error */ }
  };

  const parentOptions = (excludeId: string) => store.items.filter((c) => c.entity?.id !== excludeId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('task.categories.title')}</CardTitle>
        <CardDescription>{t('task.categories.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

        {store.isLoading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />)}</div>
        ) : store.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.categories.empty')}</p>
        ) : (
          <div className="space-y-2">
            {store.items.map((cat) => {
              const id = cat.entity?.id || '';
              const isEditing = editingId === id;
              return (
                <div key={id} className="rounded-md border border-border">
                  <div className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{cat.name}</span>
                      {cat.code && <Badge variant="secondary" size="sm">{cat.code}</Badge>}
                      {cat.task_count > 0 && <span className="text-xs text-muted-foreground">{t('task.categories.taskCount', { count: cat.task_count })}</span>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isEditing ? (
                        <>
                          <Button size="sm" isLoading={store.isSaving} onClick={() => saveEdit(id)}>{t('common.save')}</Button>
                          <Button size="sm" variant="ghost" onClick={cancelEdit}>{t('common.cancel')}</Button>
                        </>
                      ) : confirmId === id ? (
                        <>
                          <Button size="sm" variant="danger" isLoading={store.isDeleting} onClick={() => remove(id)}>{t('common.confirm')}</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>{t('common.cancel')}</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => startEdit(id)}>{t('common.edit')}</Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmId(id)}>{t('common.delete')}</Button>
                        </>
                      )}
                    </div>
                  </div>

                  {isEditing && editForm && (
                    <div className="space-y-3 border-t border-border px-3 py-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input label={t('task.categories.name')} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                        <Input label={t('task.categories.code')} value={editForm.code} onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} />
                      </div>
                      <Input label={t('task.field.description')} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.categories.parent')}</label>
                          <Select value={editForm.parent_id} onChange={(e) => setEditForm({ ...editForm, parent_id: e.target.value })}>
                            <option value="">{t('task.noParent')}</option>
                            {parentOptions(id).map((c) => (
                              <option key={c.entity?.id} value={c.entity?.id || ''}>{c.name}</option>
                            ))}
                          </Select>
                        </div>
                        <Input
                          label={t('task.categories.sortOrder')}
                          value={editForm.sort_order}
                          onChange={(e) => setEditForm({ ...editForm, sort_order: digitsOnly(e.target.value) })}
                          inputMode="numeric"
                        />
                      </div>
                      <div className="pt-2 border-t border-border">
                        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('task.categories.defaults')}</span>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.categories.defaultTaskType')}</label>
                            <Select value={editForm.default_task_type_id} onChange={(e) => setEditForm({ ...editForm, default_task_type_id: e.target.value })}>
                              <option value="">{t('common.none')}</option>
                              {taskTypeStore.items.map((tt: any) => (
                                <option key={tt.entity?.id} value={tt.entity?.id || ''}>{tt.display_name}</option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.categories.defaultNumbering')}</label>
                            <Select value={editForm.default_numbering_scheme_id} onChange={(e) => setEditForm({ ...editForm, default_numbering_scheme_id: e.target.value })}>
                              <option value="">{t('common.none')}</option>
                              {numberingStore.items.map((s) => (
                                <option key={s.entity?.id} value={s.entity?.id || ''}>{s.name}</option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.defaultPriority')}</label>
                            <Select value={editForm.default_priority} onChange={(e) => setEditForm({ ...editForm, default_priority: e.target.value })}>
                              <option value="">{t('task.allPriorities')}</option>
                              {taskPriorityOptions.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
                            </Select>
                          </div>
                        </div>
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-foreground mb-1.5">{t('task.categories.defaultPipeline')}</label>
                          <PipelinePicker value={editForm.default_pipeline_id} onChange={(id) => setEditForm({ ...editForm, default_pipeline_id: id })} className="sm:w-72" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-medium text-foreground">{t('task.categories.addNew')}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label={t('task.categories.name')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('task.categories.namePlaceholder')} />
            <Input label={t('task.categories.code')} value={code} onChange={(e) => setCode(e.target.value)} placeholder="OPS" />
          </div>
          <Input label={t('task.field.description')} value={description} onChange={(e) => setDescription(e.target.value)} />
          <div className="flex justify-end">
            <Button onClick={create} disabled={!name.trim()} isLoading={store.isSaving}>{t('task.categories.add')}</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export default TaskCategoriesAdmin;
